import { api } from '@/config/api';

export interface UserProgressResponse {
  totalPoints: number;
  completedQuizzes: string[];
  updatedAt: string | Date;
  gaps: Record<string, {
    userId: string;
    gapId: string;
    discoveredVia: string[];
    discoveredViaNames: string[];  // Stakeholder names for display
    quizAttempts: number;
    quizPassed: boolean;
    pointsEarned: number;
    lastAttemptAt?: string | Date;
  }>
}

class UserProgressService {
  async getProgress(userId: string) {
    const res = await api.get<UserProgressResponse>(`/users/${userId}/progress`);
    if (!res.success || !res.data) throw new Error('Failed to load user progress');
    return res.data;
  }

  async recordDiscovery(userId: string, gapId: string, stakeholderId: string) {
    const res = await api.post(`/users/${userId}/progress/gaps/${gapId}/discovery`, { stakeholderId });
    if (!res.success) throw new Error('Failed to record discovery');
    return true;
  }

  async recordAttempt(userId: string, gapId: string, correct: boolean, pointsEarned: number) {
    const res = await api.post<{ totalPoints: number; completedQuizzes: string[] }>(
      `/users/${userId}/progress/gaps/${gapId}/attempts`,
      { correct, pointsEarned }
    );
    if (!res.success || !res.data) throw new Error('Failed to record attempt');
    return res.data;
  }

  /**
   * Server-side quiz answer validation (#65).
   * Sends the selected answer to the server for grading instead of grading client-side.
   * The server also records the attempt in user progress.
   */
  async validateQuizAnswer(
    quizId: string,
    gapId: string,
    questionIndex: number,
    selectedIndex: number,
    attemptNumber: number
  ): Promise<{
    correct: boolean;
    pointsEarned: number;
    explanation: string;
    correctAnswerIndex: number;
    totalPoints: number;
    completedQuizzes: string[];
  }> {
    const res = await api.post<{
      correct: boolean;
      pointsEarned: number;
      explanation: string;
      correctAnswerIndex: number;
      totalPoints: number;
      completedQuizzes: string[];
    }>('/stakeholders/quiz/validate', {
      quizId,
      gapId,
      questionIndex,
      selectedIndex,
      attemptNumber,
    });
    if (!res.success || !res.data) {
      throw new Error((res as any).error || 'Failed to validate quiz answer');
    }
    return res.data;
  }
}

export const userProgressService = new UserProgressService();

